import React, { useEffect, useState } from "react";
import { Switch } from "../../ToggleSwitch/SmallToggleSwitch";
import axios from "axios";
import Modal from "react-modal";
import style from "./SecuritySettings.module.scss";
import { getImageUrl } from "../../../../commons/assets_path";
import { PageLoader } from "../../Loaders/PageLoader";
import { Loader } from "../../Loaders/Loader";
import { useTranslation } from "react-i18next";

type Props = {
  client: {
    id: number;
    code: string;
    plan_code: string;
  };
  changeModalCss?: (width: string, height: string) => void;
  setIsMenuOpen?: (isOpen: boolean) => void;
  setHeaderShow?: (show: boolean) => void;
};

type Mode = "default" | "saml" | "ipAccess";

const twoFactorAuthModalStyles: Modal.Styles = {
  overlay: {
    zIndex: 1501,
    background: "rgba(34,34,34, 0.3)",
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: 600,
    width: "100%",
    borderRadius: 12,
    padding: 0,
  },
};

const SecuritySettings = ({ client, changeModalCss, setIsMenuOpen, setHeaderShow }: Props) => {
  const { t } = useTranslation();

  const [mode, setMode] = useState<Mode>("default");
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [is2FAEnabledModal, setIs2FAEnabledModal] = useState(false);
  const [is2FADisableModal, setIs2FADisableModal] = useState(false);
  const [isIPAccessModal, setIsIPAccessModal] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState<{
    client_id: number;
    idp_entity_id: string;
    idp_sso_service_url: string;
    idp_cert: {};
    allow_create_member: boolean;
    use_saml_only: boolean;
    enable: boolean;
  }>({
    client_id: client.id,
    idp_entity_id: "",
    idp_sso_service_url: "",
    idp_cert: {},
    allow_create_member: true,
    use_saml_only: true,
    enable: true,
  });
  const [IPAccessList, setIPAccessList] = useState<
    { client_id: number; _destroy: boolean; id: string | null; address: string; cidr: string; description: string }[]
  >([]);
  const [isSAMLEnabled, setIsSAMLEnabled] = useState(false);
  const [img, setImg] = useState<{ data: string | ArrayBuffer | null; name: string; file: File | null }>({
    data: null,
    name: "",
    file: null,
  });
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(true);

  const closeModal = () => {
    setMode("default");
    setIsMenuOpen?.(true);
    changeModalCss?.("100%", "auto");
    setHeaderShow?.(true);
  };

  const openModal = (newMode: Mode) => {
    setMode(newMode);
    setIsMenuOpen?.(false);
    changeModalCss?.("800px", "auto");
    setHeaderShow?.(false);
  };

  const get2FAStatus = async () => {
    const response = await axios.get(`/clients/${client.id}/two_factor_auth_status`);
    setIs2FAEnabled(response.data.two_factor_auth_status);
    setPageLoading(false);
  };

  const handleFileChange = (file: File) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImg({
          data: reader.result,
          name: file.name,
          file: file,
        });
        setFormData((prevData) => ({
          ...prevData,
          idp_cert: file,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const getSamlSetting = async () => {
    const response = await axios.get(`/${client.code}/settings/login_setting/retrieve`);
    setFormData({ ...formData, ...response.data });
    if (response.data.idp_entity_id) {
      setIsSAMLEnabled(true);
    }
    setImg({ ...img, name: response.data.idp_cert_filename });
  };
  const patchSAMLStatus = async () => {
    setBtnLoading(true);
    const form = new FormData();
    // formData オブジェクトの各プロパティを FormData に追加
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "idp_cert") {
        form.append(`saml_setting[${key}]`, value.toString());
      }
    });

    // idp_cert ファイルを別途追加
    if (formData.idp_cert instanceof File) {
      form.append("saml_setting[idp_cert]", formData.idp_cert, formData.idp_cert.name);
    }
    try {
      if (!isSAMLEnabled) {
        const response = await axios.post(`/${client.code}/settings/login_setting`, form, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        setMessage(response.data.message);
      } else {
        const response = await axios.patch(`/${client.code}/settings/login_setting`, form, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        setMessage(response.data.message);
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        setErrorMessage(error.response.data.message);
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const patch2FAStatus = async (is2FA: boolean) => {
    setBtnLoading(true);
    const response = await axios.patch(`/clients/${client.id}/update_is_forced_two_factor_auth`, {
      client: {
        is_forced_two_factor_auth: is2FA,
      },
    });
    if (response.status === 200) {
      setMessage(response.data.message);
      setIs2FAEnabledModal(false);
      setIs2FADisableModal(false);
      get2FAStatus();
    }
    setBtnLoading(false);
  };

  const getIPAccess = async () => {
    const response = await axios.get(`/${client.code}/settings/secure_ip/retrieve`);
    setIPAccessList(Array.isArray(response.data.secure_ips) ? response.data.secure_ips : []);
  };

  const patchIPAccess = async () => {
    setBtnLoading(true);
    const secureIpsAttributes = IPAccessList.reduce<Record<number, (typeof IPAccessList)[0]>>((acc, item, index) => {
      acc[index] = item;
      return acc;
    }, {});

    await axios
      .patch(`/${client.code}/settings/secure_ip`, {
        client: {
          secure_ips_attributes: secureIpsAttributes,
        },
      })
      .then((response) => {
        getIPAccess();
        setMessage(response.data.message);
        setIsIPAccessModal(false);
      })
      .catch((error) => {
        setErrorMessage(error.response.data.message);
      })
      .finally(() => {
        setBtnLoading(false);
      });
  };

  const addNewIPAddress = () => {
    setIPAccessList((prevList) => {
      const currentList = Array.isArray(prevList) ? prevList : [];
      return [
        ...currentList,
        { client_id: client.id, _destroy: false, id: null, address: "", cidr: "", description: "" },
      ];
    });
  };

  const fileAttachStyle = (e: React.DragEvent<HTMLElement>) => {
    let dragArea: HTMLElement | null = null;
    const target = e.target as HTMLElement;
    if (target.classList.contains("drag-area")) {
      dragArea = target;
    } else if (target.parentElement?.classList.contains("drag-area")) {
      dragArea = target.parentElement;
    }

    if (dragArea && dragArea.classList.contains("dragover")) {
      if (
        e.type === "drop" ||
        (e.type === "dragleave" &&
          !target.parentElement?.classList.contains("drag-area") &&
          (e.relatedTarget === null ||
            !(e.relatedTarget as HTMLElement).parentElement?.classList.contains("drag-area")))
      ) {
        dragArea.classList.remove("dragover");
      }
    } else if (dragArea && e.type === "dragover") {
      dragArea.classList.add("dragover");
    }
  };

  const handle2FAToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setIs2FAEnabledModal(true);
    } else {
      setIs2FADisableModal(true);
    }
  };

  useEffect(() => {
    if (isIPAccessModal) {
      getIPAccess();
    }
  }, [isIPAccessModal]);

  useEffect(() => {
    get2FAStatus();
  }, []);

  useEffect(() => {
    if (message !== "") {
      setTimeout(() => {
        setMessage("");
      }, 7000);
    }
    if (errorMessage !== "") {
      setTimeout(() => {
        setErrorMessage("");
      }, 7000);
    }
  }, [message, errorMessage]);

  useEffect(() => {
    if (mode === "saml") {
      getSamlSetting();
    }
  }, [mode]);

  const renderContent = () => {
    switch (mode) {
      case "saml":
        return (
          <div className={style.actionModal}>
            {errorMessage && (
              <div className="flash_message_for_alert_message alert" style={{ zIndex: 9999 }}>
                {errorMessage}
              </div>
            )}
            <div>
              <div className={style.modalTitle} data-testid="test-saml-modal-title">
                {t("SecuritySettings.SAML認証によるSSOの設定")}
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
              <div className={style.modalText}>{t("SecuritySettings.Identity Providerの識別子")}</div>
              <div className={style.input}>
                <input
                  type="text"
                  placeholder={t("SecuritySettings.識別子")}
                  style={{ width: "100%" }}
                  value={formData.idp_entity_id}
                  onChange={(e) => setFormData({ ...formData, idp_entity_id: e.target.value })}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
              <div className={style.modalText}>{t("SecuritySettings.Identity ProviderのSSOエンドポイントURL")}</div>
              <div className={style.input}>
                <input
                  type="text"
                  placeholder={t("SecuritySettings.URL")}
                  style={{ width: "100%" }}
                  value={formData.idp_sso_service_url}
                  onChange={(e) => setFormData({ ...formData, idp_sso_service_url: e.target.value })}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
              <div className={style.modalText}>
                {t("SecuritySettings.Identity Providerが署名に使用する公開鍵の証明書")}
              </div>
              {img.name ? (
                <div id="img-name" className={style.fileAttachWrap}>
                  <div className={`${style.fileAttachFiled} drag-area`}>
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        WebkitMask: `url(${getImageUrl(
                          "images/icons/table_data_types/file.svg",
                        )}) no-repeat center / contain`,
                        mask: `url(${getImageUrl(
                          "images/icons/table_data_types/file.svg",
                        )}) no-repeat center / contain`,
                        backgroundColor: "#222222",
                      }}
                    />
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {img.name}
                    </div>
                  </div>
                  <img
                    src={getImageUrl("images/icons/cancel.svg")}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setImg({ data: null, name: "", file: null });
                      setFormData((prevData) => ({ ...prevData, idp_cert: {} }));
                    }}
                  />
                </div>
              ) : (
                <label
                  id="certificate"
                  className={`${style.fileAttachBtn} drag-area`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    fileAttachStyle(e);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    fileAttachStyle(e);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    fileAttachStyle(e);
                    const file = e.dataTransfer.files[0];
                    if (file && /\.(crt|cer|cert)$/i.test(file.name)) {
                      handleFileChange(file);
                    } else {
                      alert(t("SecuritySettings.allowedCertificateFileTypesMessage"));
                    }
                  }}
                >
                  <input
                    type="file"
                    id="file-input"
                    style={{ display: "none" }}
                    accept=".crt,.cer,.cert"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files.length > 0) {
                        handleFileChange(files[0]);
                      }
                    }}
                  />
                  {t("SecuritySettings.ファイルを選択")}
                </label>
              )}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Switch
                active={formData.allow_create_member}
                setActive={() => setFormData({ ...formData, allow_create_member: !formData.allow_create_member })}
                disabled={false}
                propHandle={() => {}}
              />
              <div className={style.modalText}>
                {t("SecuritySettings.ジャストインタイムプロビジョニングを有効化する")}
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Switch
                active={formData.use_saml_only}
                setActive={() => setFormData({ ...formData, use_saml_only: !formData.use_saml_only })}
                disabled={false}
                propHandle={() => {}}
              />
              <div className={style.modalText}>
                {t("SecuritySettings.ログイン時にSAML認証だけを使うように制限する")}
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Switch
                active={formData.enable}
                setActive={() => setFormData({ ...formData, enable: !formData.enable })}
                disabled={false}
                propHandle={() => {}}
              />
              <div className={style.modalText}>{t("SecuritySettings.Saml設定を有効化する")}</div>
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button className="btn btn-secondary" onClick={closeModal}>
                {t("SecuritySettings.戻る")}
              </button>
              <button
                className="btn btn-info d-flex align-items-center justify-content-center"
                onClick={() => patchSAMLStatus()}
              >
                {btnLoading ? <Loader /> : t("SecuritySettings.変更")}
              </button>
            </div>
          </div>
        );

      case "ipAccess":
        return (
          <div className={style.actionModal}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div className={style.modalTitle} data-testid="test-ipaddress-modal-title">
                {t("SecuritySettings.IPアドレスによるアクセス制限")}
              </div>
              <div className={style.modalDescription}>
                {t(
                  "SecuritySettings.IPアドレスを登録すると、登録したIPアドレス以外からのアクセスができなくなります。「CIDR」に「1」～「32」ビットの数値を指定することで範囲指定が可能です。",
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
                <div style={{ fontSize: "12px", fontWeight: "600", width: "270px" }}>
                  {t("SecuritySettings.IPアドレス")}
                </div>
                <div style={{ fontSize: "12px", fontWeight: "600", width: "93px" }}>{t("SecuritySettings.CIDR")}</div>
                <div style={{ fontSize: "12px", fontWeight: "600" }}>{t("SecuritySettings.メモ")}</div>
              </div>
              {IPAccessList?.map((item, index) => (
                <div key={index} style={{ display: item._destroy ? "none" : "flex" }}>
                  <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
                    <div className={style.input} style={{ width: "243px", height: "45px" }}>
                      <input
                        type="text"
                        placeholder={t("SecuritySettings.IPアドレス")}
                        value={item.address}
                        onChange={(e) => {
                          const updatedList = IPAccessList.map((listItem, idx) =>
                            idx === index ? { ...listItem, address: e.target.value } : listItem,
                          );
                          setIPAccessList(updatedList);
                        }}
                        disabled={item.id !== null}
                        style={{ color: item.id !== null ? "#777777" : "black", backgroundColor: "transparent" }}
                      />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", paddingLeft: "10px" }}>/</div>
                    <div className={style.input} style={{ width: "93px", height: "45px" }}>
                      <input
                        type="text"
                        placeholder={t("SecuritySettings.CIDR")}
                        style={{
                          maxWidth: "100%",
                          color: item.id !== null ? "#777777" : "black",
                          backgroundColor: "transparent",
                        }}
                        value={item.cidr}
                        onChange={(e) => {
                          const updatedList = IPAccessList.map((listItem, idx) =>
                            idx === index ? { ...listItem, cidr: e.target.value } : listItem,
                          );
                          setIPAccessList(updatedList);
                        }}
                        disabled={item.id !== null}
                      />
                    </div>
                    <div className={style.input} style={{ width: "313px", height: "45px" }}>
                      <input
                        type="text"
                        placeholder={t("SecuritySettings.メモ")}
                        value={item.description}
                        onChange={(e) => {
                          const updatedList = IPAccessList.map((listItem, idx) =>
                            idx === index ? { ...listItem, description: e.target.value } : listItem,
                          );
                          setIPAccessList(updatedList);
                        }}
                        disabled={item.id !== null}
                        style={{ color: item.id !== null ? "#777777" : "black", backgroundColor: "transparent" }}
                      />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <img
                        src={getImageUrl("images/icons/cancel.svg")}
                        onClick={() => {
                          const updatedList = IPAccessList.map((listItem, idx) =>
                            idx === index ? { ...listItem, _destroy: true } : listItem,
                          );
                          setIPAccessList(updatedList);
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", flexDirection: "row", gap: "8px", cursor: "pointer" }}>
                <div style={{ fontSize: "12px", fontWeight: "600", color: "#2D6295" }} onClick={addNewIPAddress}>
                  {t("SecuritySettings.＋IPアドレスを追加する")}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "row", gap: "8px", justifyContent: "flex-end" }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  closeModal();
                  setIPAccessList([]);
                }}
              >
                {t("SecuritySettings.戻る")}
              </button>
              <button
                className="btn btn-info d-flex align-items-center justify-content-center"
                onClick={() => patchIPAccess()}
              >
                {btnLoading ? <Loader /> : t("SecuritySettings.保存")}
              </button>
            </div>
          </div>
        );

      default:
        return (
          <>
            {message && (
              <div className="flash_message notice" style={{ zIndex: 9999 }}>
                {message}
              </div>
            )}
            {errorMessage && (
              <div className="flash_message_for_alert_message alert" style={{ zIndex: 9999 }}>
                {errorMessage}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "40px" }}>
              <div style={{ fontSize: "20px", fontWeight: "600" }} data-testid="security-settings-title">
                {t("SecuritySettings.セキュリティ")}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ fontSize: "14px", fontWeight: "600" }} data-testid="test-section-ipaddress">
                    {t("SecuritySettings.IPアドレスによるアクセス制限")}
                  </div>
                  <div style={{ fontSize: "12px", color: "#777777" }}>
                    {t("SecuritySettings.YoomにアクセスできるIPアドレスを制限します。")}
                  </div>
                </div>
                <button
                  className="btn btn-info btn-sm-size"
                  style={{
                    minWidth: "104px",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: "600",
                    padding: "0",
                    height: "36px",
                  }}
                  onClick={(e) => {
                    e.currentTarget.blur();
                    openModal("ipAccess");
                  }}
                  disabled={["free", "standard", "mini"].includes(client.plan_code)}
                >
                  {t("SecuritySettings.設定する")}
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ fontSize: "14px", fontWeight: "600" }} data-testid="test-section-saml">
                    {t("SecuritySettings.SAML認証の設定")}
                  </div>
                  <div style={{ fontSize: "12px", color: "#777777" }}>
                    {t("SecuritySettings.SAML認証によるSSOの設定が可能です。")}
                  </div>
                </div>
                <button
                  className="btn btn-info btn-sm-size"
                  style={{
                    minWidth: "104px",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: "600",
                    padding: "0",
                    height: "36px",
                  }}
                  onClick={(e) => {
                    e.currentTarget.blur();
                    openModal("saml");
                  }}
                  disabled={["free", "standard", "mini", "team"].includes(client.plan_code)}
                  data-testid="test-setting-saml-btn"
                >
                  {t("SecuritySettings.設定する")}
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ fontSize: "14px", fontWeight: "600" }} data-testid="test-2fa-section">
                    {t("SecuritySettings.2要素認証の設定")}
                  </div>
                  <div style={{ fontSize: "12px", color: "#777777" }}>
                    {t("SecuritySettings.ワークスペースログイン時に2要素認証を必須にします。")}
                    <br />
                    {t(
                      "SecuritySettings.有効化したタイミングから2要素認証を有効化していないユーザーは2要素認証設定ページ以外に閲覧が不可となります。",
                    )}
                  </div>
                </div>
                <Switch
                  active={is2FAEnabled}
                  setActive={() => {}}
                  disabled={["free", "standard", "mini", "team"].includes(client.plan_code)}
                  propHandle={handle2FAToggle}
                />
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <>
      {pageLoading ? (
        <PageLoader />
      ) : (
        <>
          {renderContent()}
          <Modal
            isOpen={is2FAEnabledModal}
            onRequestClose={() => setIs2FAEnabledModal(false)}
            style={twoFactorAuthModalStyles}
          >
            <div className={style.modal}>
              <div className={style.modalTitle} data-testid="test-2fa-auth-modal-title">
                {t("SecuritySettings.全てのユーザーに2要素認証を必須にします。")}
              </div>
              <div>
                {t("SecuritySettings.ワークスペースへのログイン時に、2要素認証を必須にします。")}
                <br />
                {t(
                  "SecuritySettings.有効化したタイミングから2要素認証を有効化していないユーザーは2要素認証設定ページ以外の閲覧が不可となります。",
                )}
                <br />
                {t(
                  "SecuritySettings.2要素認証を有効化していないユーザーはメールが送信されますので、2要素認証の設定を行うように周知してください。",
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "row", gap: "9px", justifyContent: "flex-end" }}>
                <button
                  className="btn btn-secondary"
                  style={{ width: "100%" }}
                  onClick={() => {
                    setIs2FAEnabledModal(false);
                  }}
                >
                  {t("SecuritySettings.キャンセル")}
                </button>
                <button
                  className="btn btn-info d-flex align-items-center justify-content-center"
                  style={{ width: "100%" }}
                  onClick={() => patch2FAStatus(true)}
                >
                  {btnLoading ? <Loader /> : t("SecuritySettings.変更")}
                </button>
              </div>
            </div>
          </Modal>
          <Modal
            isOpen={is2FADisableModal}
            onRequestClose={() => setIs2FADisableModal(false)}
            style={twoFactorAuthModalStyles}
          >
            <div className={style.modal}>
              <div className={style.modalTitle}>
                {t("SecuritySettings.本当に全てのユーザーの2要素認証必須を解除しますか。")}
              </div>
              <div style={{ display: "flex", flexDirection: "row", gap: "9px", justifyContent: "flex-end" }}>
                <button
                  className="btn btn-secondary"
                  style={{ width: "100%" }}
                  onClick={() => {
                    setIs2FADisableModal(false);
                  }}
                >
                  {t("SecuritySettings.キャンセル")}
                </button>
                <button
                  className="btn btn-info d-flex align-items-center justify-content-center"
                  style={{ width: "100%" }}
                  onClick={() => patch2FAStatus(false)}
                >
                  {btnLoading ? <Loader /> : t("SecuritySettings.変更")}
                </button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default SecuritySettings;
